import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, Chip } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#bf360c';
const PRIMARY_MID = '#e64a19';
const PRIMARY_LT  = '#fbe9e7';

const fmtQ  = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
    if (!d) return '-';
    const s = String(d).split('T')[0];
    const [y, m, day] = s.split('-');
    return `${day}/${m}/${y}`;
};
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const STATUS_COLOR = {
    'Pending':   { bg: '#fff3e0', color: '#e65100' },
    'Partial':   { bg: '#e3f2fd', color: '#0277bd' },
    'Completed': { bg: '#e8f5e9', color: '#2e7d32' },
    'Cancelled': { bg: '#fce4ec', color: '#c62828' },
};

const statusChip = (status) => {
    const s = STATUS_COLOR[status] || { bg: '#f1f5f9', color: '#475569' };
    return (
        <span style={{ background: s.bg, color: s.color, borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
            {status || '-'}
        </span>
    );
};

const OrderAnalysisReport = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate]     = useState(fmt2Date(today));
    const [data, setData]         = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [fetched, setFetched]   = useState(false);
    const [search, setSearch]     = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    useEffect(() => {
        if (companyId && branchId && token) fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'PurchaseAnalysisReportApi',
                `/OrderAnalysisReport/${fromDate}/${toDate}/${companyId}/${branchId}`,
                token
            );
            const rows = res?.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch order analysis report');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        let rows = data;
        if (statusFilter !== 'All') rows = rows.filter(r => r.OrderStatus === statusFilter);
        if (search.trim()) {
            const q = search.toLowerCase();
            rows = rows.filter(r =>
                (r.PurchaseOrderNo || '').toLowerCase().includes(q) ||
                (r.SupplierName    || '').toLowerCase().includes(q) ||
                (r.ItemName        || '').toLowerCase().includes(q)
            );
        }
        return rows;
    }, [data, search, statusFilter]);

    const totals = useMemo(() => ({
        orderQty:    filtered.reduce((s, r) => s + (parseFloat(r.OrderQty)    || 0), 0),
        receivedQty: filtered.reduce((s, r) => s + (parseFloat(r.ReceivedQty) || 0), 0),
        billedQty:   filtered.reduce((s, r) => s + (parseFloat(r.BilledQty)   || 0), 0),
        returnQty:   filtered.reduce((s, r) => s + (parseFloat(r.ReturnQty)   || 0), 0),
        pendingQty:  filtered.reduce((s, r) => s + (parseFloat(r.PendingQty)  || 0), 0),
    }), [filtered]);

    const statusCounts = useMemo(() => {
        const map = {};
        const seen = {};
        data.forEach(r => {
            const key = r.PurchaseOrderId || r.PurchaseOrderNo;
            if (!seen[key]) {
                seen[key] = true;
                map[r.OrderStatus] = (map[r.OrderStatus] || 0) + 1;
            }
        });
        return map;
    }, [data]);

    const uniqueOrderCount = useMemo(() => {
        const ids = new Set(data.map(r => r.PurchaseOrderId || r.PurchaseOrderNo));
        return ids.size;
    }, [data]);

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'OrderAnalysisReport', 0);
        if (!filtered.length) return;
        const headers = ['#', 'PO No', 'PO Date', 'Supplier', 'Item', 'Order Qty', 'Received Qty', 'Billed Qty', 'Return Qty', 'Pending Qty', 'Status'];
        const rows = filtered.map((r, i) => [
            i + 1,
            r.PurchaseOrderNo || '',
            fmtDate(r.PurchaseOrderDate),
            r.SupplierName || '',
            r.ItemName || '',
            parseFloat(r.OrderQty)    || 0,
            parseFloat(r.ReceivedQty) || 0,
            parseFloat(r.BilledQty)   || 0,
            parseFloat(r.ReturnQty)   || 0,
            parseFloat(r.PendingQty)  || 0,
            r.OrderStatus || '',
        ]);
        const totalRow = ['', '', '', '', 'Totals',
            totals.orderQty, totals.receivedQty, totals.billedQty, totals.returnQty, totals.pendingQty, ''];

        await exportStyledExcel({
            reportTitle: 'ORDER ANALYSIS REPORT',
            sheetName: 'Order Analysis',
            fileName: `OrderAnalysisReport_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            totalsRow: totalRow,
            columnWidths: [4, 14, 12, 22, 28, 12, 14, 12, 12, 12, 12],
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap', border: `1px solid ${PRIMARY_MID}`, color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };
    const tdR    = { ...tdBase, textAlign: 'right' };

    const STATUS_TABS = ['All', 'Pending', 'Partial', 'Completed', 'Cancelled'];

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading order analysis report..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Order Analysis Report
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Purchase Analysis</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <input
                        placeholder="Search PO No / Supplier / Item..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 200, outline: 'none' }}
                    />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary cards */}
                {fetched && data.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Total Orders',   value: uniqueOrderCount,         color: PRIMARY },
                            { label: 'Order Qty',      value: fmtQ(totals.orderQty),    color: '#374151' },
                            { label: 'Received Qty',   value: fmtQ(totals.receivedQty), color: '#0277bd' },
                            { label: 'Billed Qty',     value: fmtQ(totals.billedQty),   color: '#7b1fa2' },
                            { label: 'Return Qty',     value: fmtQ(totals.returnQty),   color: '#c62828' },
                            { label: 'Pending Qty',    value: fmtQ(totals.pendingQty),  color: '#e65100' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Status filter tabs */}
                {fetched && data.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {STATUS_TABS.map(s => {
                            const count = s === 'All' ? uniqueOrderCount : (statusCounts[s] || 0);
                            const active = statusFilter === s;
                            return (
                                <button key={s} onClick={() => setStatusFilter(s)}
                                    style={{ border: `1px solid ${active ? PRIMARY : '#e2e8f0'}`, borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: active ? 700 : 400, background: active ? PRIMARY_LT : '#fff', color: active ? PRIMARY : '#64748b', cursor: 'pointer' }}>
                                    {s} <span style={{ fontSize: 10, opacity: 0.7 }}>({count})</span>
                                </button>
                            );
                        })}
                        <span style={{ fontSize: 11, color: '#94a3b8', alignSelf: 'center', marginLeft: 4 }}>
                            {fromDate} to {toDate} &nbsp;|&nbsp; {uniqueOrderCount} rows
                            {search && ` (filtered from ${uniqueOrderCount})`}
                        </span>
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)', scrollbarWidth: 'thin', scrollbarColor: `${PRIMARY} #f0f0f0` }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 900 }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                            <tr style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)` }}>
                                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                                <th style={{ ...thBase, minWidth: 120 }}>PO No</th>
                                <th style={{ ...thBase, minWidth: 90 }}>PO Date</th>
                                <th style={{ ...thBase, minWidth: 160 }}>Supplier</th>
                                <th style={{ ...thBase, minWidth: 180 }}>Item</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>Order Qty</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 100 }}>Received Qty</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>Billed Qty</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>Return Qty</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90, background: '#7f1d1d' }}>Pending Qty</th>
                                <th style={{ ...thBase, textAlign: 'center', minWidth: 90 }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={11} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : (() => {
                                // Group consecutive rows by PurchaseOrderId
                                const groups = [];
                                filtered.forEach(r => {
                                    const last = groups[groups.length - 1];
                                    if (last && last.orderId === r.PurchaseOrderId) {
                                        last.rows.push(r);
                                    } else {
                                        groups.push({ orderId: r.PurchaseOrderId, rows: [r] });
                                    }
                                });

                                return groups.map((grp, gi) => {
                                    const bg = gi % 2 === 0 ? '#fff' : PRIMARY_LT;
                                    return grp.rows.map((r, ri) => {
                                        const isFirst = ri === 0;
                                        const span = grp.rows.length;
                                        const pending = parseFloat(r.PendingQty) || 0;
                                        return (
                                            <tr key={`${r.PurchaseOrderId}-${r.ItemId}-${ri}`} style={{ background: bg }}>
                                                {isFirst && (
                                                    <>
                                                        <td rowSpan={span} style={{ ...tdBase, textAlign: 'center', color: '#94a3b8', verticalAlign: 'middle', borderRight: '2px solid #e2e8f0' }}>{gi + 1}</td>
                                                        <td rowSpan={span} style={{ ...tdBase, fontWeight: 600, color: PRIMARY, verticalAlign: 'middle', borderRight: '2px solid #e2e8f0' }}>{r.PurchaseOrderNo || '-'}</td>
                                                        <td rowSpan={span} style={{ ...tdBase, color: '#64748b', verticalAlign: 'middle', borderRight: '2px solid #e2e8f0' }}>{fmtDate(r.PurchaseOrderDate)}</td>
                                                        <td rowSpan={span} style={{ ...tdBase, color: '#1e293b', verticalAlign: 'middle', borderRight: '2px solid #e2e8f0' }}>{r.SupplierName || '-'}</td>
                                                    </>
                                                )}
                                                <td style={{ ...tdBase, color: '#374151' }}>{r.ItemName || '-'}</td>
                                                <td style={{ ...tdR, color: '#374151' }}>{fmtQ(r.OrderQty)}</td>
                                                <td style={{ ...tdR, color: '#0277bd' }}>{fmtQ(r.ReceivedQty)}</td>
                                                <td style={{ ...tdR, color: '#7b1fa2' }}>{fmtQ(r.BilledQty)}</td>
                                                <td style={{ ...tdR, color: '#c62828' }}>{fmtQ(r.ReturnQty)}</td>
                                                <td style={{ ...tdR, fontWeight: 700, color: pending > 0 ? '#e65100' : '#2e7d32' }}>{fmtQ(pending)}</td>
                                                <td style={{ ...tdBase, textAlign: 'center' }}>{statusChip(r.OrderStatus)}</td>
                                            </tr>
                                        );
                                    });
                                });
                            })()}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700, position: 'sticky', bottom: 0 }}>
                                    <td colSpan={5} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#374151' }}>{fmtQ(totals.orderQty)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#0277bd' }}>{fmtQ(totals.receivedQty)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#7b1fa2' }}>{fmtQ(totals.billedQty)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#c62828' }}>{fmtQ(totals.returnQty)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#e65100' }}>{fmtQ(totals.pendingQty)}</td>
                                    <td style={tdBase}></td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default OrderAnalysisReport;
